import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from './Header';
import api, { authService } from '../services/api';
import './Auth.css';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Code entry for SMS flow (when no token in URL)
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    // No redirect when no token - we show the "enter code" form instead
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate passwords match
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/auth/reset-password', {
        token,
        password
      });
      
      toast.success(response.data.message || 'Password reset successfully!');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          'Failed to reset password. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!email.trim() || !code.trim()) {
      toast.error('Please enter your email and the 6-digit code.');
      return;
    }
    if (code.length !== 6) {
      toast.error('Code must be 6 digits.');
      return;
    }
    setCodeLoading(true);
    try {
      const data = await authService.verifyResetCode(email.trim(), code.trim());
      if (data.token) {
        setSearchParams({ token: data.token });
        toast.success('Code verified. Set your new password below.');
      }
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Invalid or expired code. Request a new one from the forgot password page.';
      toast.error(msg);
    } finally {
      setCodeLoading(false);
    }
  };

  // No token: show "enter email + SMS code" form
  if (!token) {
    return (
      <div>
        <Header />
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h1>Reset Password</h1>
              <p>Enter the 6-digit code we sent to your phone</p>
            </div>
            <form onSubmit={handleVerifyCode} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Your account email"
                  disabled={codeLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="code">6-digit code</label>
                <input
                  type="text"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="000000"
                  disabled={codeLoading}
                  maxLength={6}
                  autoComplete="one-time-code"
                />
              </div>
              <button type="submit" className="auth-button" disabled={codeLoading}>
                {codeLoading ? 'Verifying...' : 'Continue'}
              </button>
            </form>
            <div className="auth-footer">
              <p>
                <Link to="/forgot-password">Request a new code</Link> · <Link to="/login">Back to login</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Password</h1>
            <p>Enter your new password</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password (min 8 characters)"
                disabled={loading}
                minLength={8}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                disabled={loading}
                minLength={8}
              />
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Remember your password? <Link to="/login">Back to login</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
